import { Fragment, useState, useEffect } from "react";
import { Button, Result, Spin, Upload } from "antd";
import { Searcher, TableAntd } from "../../components/commons";
import { openInNewTab, uploadProps } from "../../utils/functions";
import { getByType } from "../../api/participante";
import { createColumns } from "./formatTable";
import { useHistory, useParams } from "react-router-dom";
import * as SHARED from "../../utils/shared";
import { FileExcelOutlined, ReloadOutlined } from "@ant-design/icons";
import { DATE_LIKE_NAME } from "../../utils/functions";
import { downloadExcelByTipoParticipante } from "../../api/participante";
import { useAuth } from "../../utils/useAuth";

function Participante() {
  const { isAdmin } = useAuth();
  const [filterTable, setFilterTable] = useState(null);
  const [dataToTable, setDataToTable] = useState([]);
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const params = useParams();
  const participanteTipo = params.name;

  const setParticipantType = (participantType) => {
    if (participantType === "administrativo") {
      return SHARED.ID_ADMINISTRATIVO;
    }
    if (participantType === "docente") {
      return SHARED.ID_DOCENTE;
    }
    if (participantType === "estudiante") {
      return SHARED.ID_ESTUDIANTE;
    }
  };

  const fetchDataToTable = async () => {
    try {
      setIsLoading(true);
      const res = await getByType(setParticipantType(participanteTipo));
      setDataToTable(res.payload);
    } catch (error) {
      setError(error);
      setIsError(true);
      setDataToTable([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDataToTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  /* eslint-disable no-unused-vars */
  const [page, setPage] = useState(1);
  const history = useHistory();

  const handleOpenInNewTab = (nrodocumento) => {
    openInNewTab(`${SHARED.PATH_APP_PARTICIPANTE}/${nrodocumento}`);
  };

  const handleGoDetail = (nrodocumento) => {
    history.push(`${SHARED.PATH_APP_PARTICIPANTE}/${nrodocumento}`);
  };

  if (isLoading) {
    return <Spin tip="Cargando" className="w-full py-60" />;
  }

  if (isError) {
    return (
      <Result
        status="500"
        title="Algo salió mal"
        subTitle={error.message}
        extra={
          <Button onClick={() => window.location.reload()} type="primary">
            Recargar
          </Button>
        }
      />
    );
  }

  // ------ Formato para que se muestre en la tabla ------
  const columnsToTable = createColumns(page, handleOpenInNewTab, handleGoDetail);

  // ------ Search filter ------
  /**
   * search
   * @param {String} value
   * Busca por cualquier valor de la columna
   * fuentes:
   * https://codesandbox.io/s/basic-usage-ant-design-demo-2swep?file=/index.js
   * https://github.com/ant-design/ant-design/issues/22969#issuecomment-611738230
   */
  const search = (value) => {
    const tempFilterTable = dataToTable.filter((o) =>
      Object.keys(o).some((k) => String(o[k]).toLowerCase().includes(value.toLowerCase()))
    );

    setFilterTable(tempFilterTable);
  };

  /**
   * filterByState
   * ------------
   * @param {Array} dataToFilter
   * @param {String} state
   * @returns {Number}
   */
  const filterByState = (dataToFilter, state) => {
    if (!dataToFilter || dataToFilter == null) return 0;
    return dataToFilter.filter((item) => item.idestado === state).length;
  };

  const statistics = {
    noInscrito: filterByState(dataToTable, SHARED.ID_ESTADO_NO_INSCRITO),
    validado: filterByState(dataToTable, SHARED.ID_ESTADO_VALIDADO),
    pendiente: filterByState(dataToTable, SHARED.ID_ESTADO_PENDIENTE),
    sorteado: filterByState(dataToTable, SHARED.ID_ESTADO_SORTEADO),
    observado: filterByState(dataToTable, SHARED.ID_ESTADO_OBSERVADO)
  };

  const NumberStatus = ({ quantity, label, color }) => (
    <div className="text-lg font-semibold mr-4" style={{ color }}>
      {quantity} {label}
    </div>
  );

  const getParticipantType = (participantType) => {
    if (participantType === "estudiante") {
      return "ESTUDIANTES";
    }

    if (participantType === "docente") {
      return "DOCENTES";
    }

    if (participantType === "administrativo") {
      return "ADMINISTRATIVOS";
    }
  };

  // ------------- Descarga supervisores -------------
  const downloadParticipantesExcel = async () => {
    const blob = await downloadExcelByTipoParticipante(setParticipantType(participanteTipo));

    // 2. Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `participantes-${getParticipantType(participanteTipo)}-${DATE_LIKE_NAME}.xlsx`
    );
    // 3. Append to html page
    document.body.appendChild(link);
    // 4. Force download
    link.click();
    // 5. Clean up and remove the link
    link.parentNode.removeChild(link);
  };

  return (
    <Fragment>
      <div className="gap-4 mr-4 flex flex-col flex-wrap md:flex-row justify-between items-center md:flex-nowrap my-0">
        <div className="my-4 gap-4 mr-4 flex flex-col flex-wrap md:flex-row justify-start items-center md:flex-nowrap">
          <span className="font-bold text-lg text-blue-500">
            ({dataToTable.length || "0"}) {getParticipantType(participanteTipo)} &nbsp;
            <Button icon={<ReloadOutlined />} onClick={() => setReload(!reload)}></Button>
          </span>
          {isAdmin && (
            <>
              <Button
                onClick={downloadParticipantesExcel}
                icon={<FileExcelOutlined />}
                className="focus:outline-none text-white text-sm h-9 px-5 rounded-md bg-green-500 hover:bg-green-600 hover:shadow-lg"
              >
                Descargar Excel
              </Button>
              <Upload
                showUploadList={false}
                {...uploadProps({
                  name: "update_participantes",
                  action: "/participante/update-masivo",
                  makeReload: () => setReload(!reload)
                })}
                accept=".xlsx"
              >
                <Button
                  icon={<FileExcelOutlined />}
                  className="focus:outline-none text-white text-sm h-9 px-5 rounded-md bg-blue-500 hover:bg-blue-600 hover:shadow-lg"
                >
                  Actualizar por Excel
                </Button>
              </Upload>
            </>
          )}
        </div>
        <div className="flex gap-4">
          <span className="text-lg font-bold mt-2">{SHARED.PROCESO}</span>
        </div>
      </div>

      <div className="lg:flex">
        <div className="lg:flex w-full  lg:w-3/4">
          <NumberStatus quantity={statistics.validado} label="Validados" color="#2ECC71" />
          <NumberStatus quantity={statistics.noInscrito} label="No inscritos" color="gray" />
          <NumberStatus quantity={statistics.pendiente} label="Pendientes" color="skyblue" />
          <NumberStatus quantity={statistics.observado} label="Observados" color="orange" />
          <NumberStatus quantity={statistics.sorteado} label="Sorteados" color="green" />
        </div>

        <div className="w-full lg:w-1/4">
          <Searcher onSearch={search} placeholder="Busca algún supervisor" />
        </div>
      </div>

      <TableAntd
        columns={columnsToTable}
        dataSource={filterTable == null ? dataToTable : filterTable}
        setPage={setPage}
        pageSize={100}
      />
    </Fragment>
  );
}

export default Participante;
